"use server";

import { getActor } from "@/src/actions/auth-session";
import { apiFetchCustomerServer, apiFetchServer } from "@/src/lib/api/fetch.server";
import { ApiError } from "@/src/lib/api/types";
import { getQuoteFormOptionsCached as getQuoteFormOptions } from "@/src/lib/api/fetch-quote-form-options";
import {
  mapApiQuote,
  type ApiQuoteRow,
} from "@/src/lib/quotes/map-api-quote";
import type { Quote, QuoteStatus } from "@/src/types/quotes";
import type { QuoteFormOptions } from "@/src/types/quote-options";

type PaginatedQuotes = {
  data: ApiQuoteRow[];
  total: number;
  page: number;
  limit: number;
};

async function requireAdminActor() {
  const actor = await getActor();
  if (!actor?.isAdmin) throw new Error("Not authenticated");
  return actor;
}

async function requireCustomerId(): Promise<string> {
  const actor = await getActor();
  if (!actor) throw new Error("Not authenticated");
  return actor.id;
}

function formatApiError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : "Unknown error";
  }
  if (!err.errors) return err.message;
  const details = Object.values(err.errors).flat().join("; ");
  return details ? `${err.message}: ${details}` : err.message;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function normalizeValidUntil(val?: string): string | undefined {
  if (!val?.trim()) return undefined;
  const trimmed = val.trim();
  if (DATE_ONLY_RE.test(trimmed)) return `${trimmed}T23:59:59.999Z`;
  return trimmed;
}

export async function submitQuoteRequest(data: {
  packaging_type: string;
  quantity: number;
  timeline: string;
  custom_printing: boolean;
  dimensions?: string | null;
  material?: string | null;
  budget_range?: string | null;
  additional_info?: string | null;
  project_id?: string | null;
  project_snapshot_id?: string | null;
}): Promise<{ quoteNumber: string } | { error: string }> {
  try {
    const actor = await getActor();
    if (!actor) return { error: "You must be signed in to submit a quote." };

    const row = await apiFetchCustomerServer<ApiQuoteRow>("/quotes/authenticated", {
      method: "POST",
      body: JSON.stringify({
        packagingType: data.packaging_type,
        quantity: data.quantity,
        timeline: data.timeline,
        customPrinting: data.custom_printing,
        dimensions: data.dimensions ?? undefined,
        material: data.material ?? undefined,
        budgetRange: data.budget_range ?? undefined,
        additionalInfo: data.additional_info ?? undefined,
        projectId: data.project_id ?? undefined,
        projectSnapshotId: data.project_snapshot_id ?? undefined,
      }),
    });

    return { quoteNumber: row.quoteNumber };
  } catch (err) {
    return { error: formatApiError(err) };
  }
}

export async function reorderFromDeliveredOrder(
  orderId: string,
): Promise<{ quoteNumber: string } | { error: string }> {
  try {
    await requireCustomerId();
    const result = await apiFetchCustomerServer<{ quoteNumber: string }>(
      `/quotes/reorder-from-order/${orderId}`,
      { method: "POST" },
    );

    if ("error" in result) return result;
    return result;
  } catch (err) {
    return { error: err instanceof Error ? err.message : "Unknown error" };
  }
}

export async function listQuotes(filters?: {
  status?: QuoteStatus;
  search?: string;
}): Promise<Quote[]> {
  await requireAdminActor();
  const params = new URLSearchParams({ limit: "100" });
  if (filters?.status) {
    const backendStatus = filters.status === "sent" ? "quoted" : filters.status;
    params.set("status", backendStatus);
  }
  if (filters?.search) params.set("search", filters.search);
  const res = await apiFetchServer<PaginatedQuotes>(`/quotes?${params}`);
  return res.data.map(mapApiQuote);
}

export async function updateQuoteStatus(
  id: string,
  status: QuoteStatus,
): Promise<void> {
  await requireAdminActor();
  const backendStatus = status === "sent" ? "quoted" : status;
  await apiFetchServer(`/quotes/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status: backendStatus }),
  });
}

export async function updateQuotePricing(
  id: string,
  pricing: {
    base_price: number;
    setup_cost: number;
    total_price: number;
    spec_dimensions?: string;
    spec_material?: string;
    spec_printing?: string;
    spec_finish?: string;
    notes?: string;
    valid_until?: string;
    pricing_breakdown?: unknown | null;
  },
): Promise<void> {
  await requireAdminActor();
  await apiFetchServer(`/quotes/${id}/pricing`, {
    method: "PATCH",
    body: JSON.stringify({
      basePrice: pricing.base_price,
      setupCost: pricing.setup_cost,
      totalPrice: pricing.total_price,
      specDimensions: pricing.spec_dimensions,
      specMaterial: pricing.spec_material,
      specPrinting: pricing.spec_printing,
      specFinish: pricing.spec_finish,
      notes: pricing.notes,
      validUntil: normalizeValidUntil(pricing.valid_until),
      pricingBreakdown: pricing.pricing_breakdown,
    }),
  });
}

export async function listMyQuotes(): Promise<Quote[]> {
  await requireCustomerId();
  const res = await apiFetchCustomerServer<PaginatedQuotes>("/quotes/mine?limit=100");
  return res.data.map(mapApiQuote);
}

export async function acceptQuote(id: string): Promise<void> {
  await requireCustomerId();
  await apiFetchCustomerServer(`/quotes/${id}/accept`, { method: "POST" });
}

export async function rejectQuote(id: string): Promise<void> {
  await requireCustomerId();
  await apiFetchCustomerServer(`/quotes/${id}/reject`, { method: "POST" });
}

export async function counterOfferQuote(
  id: string,
  note: string,
  counterPrice?: number | null,
): Promise<void> {
  await requireCustomerId();
  await apiFetchCustomerServer(`/quotes/${id}/counter`, {
    method: "POST",
    body: JSON.stringify({
      customerCounterNote: note,
      customerCounterPrice: counterPrice && counterPrice > 0 ? counterPrice : 1,
    }),
  });
}

export async function createAdminQuote(data: {
  customerId: string;
  projectId?: string;
  contactName: string;
  contactEmail: string;
  contactCompany: string;
  contactPhone: string;
  packagingType: string;
  quantity: number;
  timeline: string;
  customPrinting: boolean;
  dimensions?: string;
  material?: string;
  additionalInfo?: string;
  basePrice: string;
  setupCost: string;
  totalPrice: string;
  specDimensions?: string;
  specMaterial?: string;
  specPrinting?: string;
  specFinish?: string;
  notes?: string;
  validUntil?: string;
}): Promise<{ quoteNumber: string }> {
  await requireAdminActor();
  const created = await apiFetchServer<ApiQuoteRow>("/quotes", {
    method: "POST",
    body: JSON.stringify({
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      contactCompany: data.contactCompany,
      contactPhone: data.contactPhone,
      packagingType: data.packagingType,
      quantity: data.quantity,
      timeline: data.timeline,
      customPrinting: data.customPrinting,
      dimensions: data.dimensions,
      material: data.material,
      additionalInfo: data.additionalInfo,
      projectId: data.projectId,
    }),
  });
  await apiFetchServer(`/quotes/${created.id}/pricing`, {
    method: "PATCH",
    body: JSON.stringify({
      basePrice: Number(data.basePrice),
      setupCost: Number(data.setupCost),
      totalPrice: Number(data.totalPrice),
      specDimensions: data.specDimensions,
      specMaterial: data.specMaterial,
      specPrinting: data.specPrinting,
      specFinish: data.specFinish,
      notes: data.notes,
      validUntil: normalizeValidUntil(data.validUntil),
    }),
  });
  await apiFetchServer(`/quotes/${created.id}/send`, { method: "POST" });
  return { quoteNumber: created.quoteNumber };
}

export async function getMyQuoteOptions(
  locale: string,
): Promise<QuoteFormOptions> {
  return getQuoteFormOptions(locale);
}

export async function discountAndResendQuote(
  quoteId: string,
  discount: {
    discount_type: "percentage" | "fixed";
    discount_value: number;
    notes?: string;
    valid_until?: string;
  },
): Promise<{ newTotalPrice: string } | { error: string }> {
  try {
    await requireAdminActor();
    const quote = await apiFetchServer<ApiQuoteRow>(`/quotes/${quoteId}`);
    const currentTotal = Number(quote.totalPrice ?? 0);
    const discountAmount =
      discount.discount_type === "percentage"
        ? (currentTotal * discount.discount_value) / 100
        : discount.discount_value;
    const updated = await apiFetchServer<ApiQuoteRow>(
      `/quotes/${quoteId}/discount`,
      {
        method: "POST",
        body: JSON.stringify({ discount: discountAmount }),
      },
    );
    return {
      newTotalPrice: String(updated.totalPrice ?? currentTotal - discountAmount),
    };
  } catch (err) {
    return { error: err instanceof Error ? err.message : "Unknown error" };
  }
}
